/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import styles from './instrument.module.css' with { type: 'css' };
import { ContextMenuStyles } from '../components'
import html from './instrument.module.html';
import patchMenu from './patch-menu.module.html';

import { Note, MIDIManager, MIDIEvent, Synthesizer, SynthPatch } from '../audio';
import { NoteRecorder, NoteHistoryDialog } from './mcc/mcc';

/**
 * ## TunePad generic instrument interface that wraps piano, drums, bass, guitar, etc.
 * ```html
 * <tunepad-instrument midi-in="false" midi-out="false"></tunepad-instrument>
 * ```
 */
export class TunePadInstrument extends HTMLElement {

    static readonly ELEMENT = "tunepad-instrument";

    static observedAttributes = [
        "midi-in",     // listen for midi input
        "midi-out"     // send midi output
    ];

    /** all of the HTML elements are contained within a shadow DOM */
    private root : ShadowRoot;

    /** used to generate note hints and recordings */
    private recorder = new NoteRecorder();

    /** midi input enabled */
    private midiIn = false;

    /** midi output enabled */
    private midiOut = false;


    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
        this.root.adoptedStyleSheets.push(ContextMenuStyles);
        this.root.adoptedStyleSheets.push(styles);
        this.root.innerHTML = html;
    }


    async connectedCallback() {
        this.buildPatchMenu();
    
        // disarm keyboard when an instrument loses focus
        this.root.querySelector('.instrument-wrapper')?.addEventListener('blur', e => {
            this.root.querySelector('#instrument')?.setAttribute('armed', 'false');
            this.emitCustomEvent('disarmed');
        });

        this.root.querySelector('.instrument-wrapper')?.addEventListener('focus', e => {
            this.root.querySelector('#instrument')?.setAttribute('armed', 'true');
            this.emitCustomEvent('armed');
        });

        // copy note hints to the clipboard
        this.root.querySelector('#note-hint')?.addEventListener('pointerdown', (e) => {
            const el = this.root.querySelector('#note-hint .code');
            if (el) {
                const hint = el.innerHTML;
                el.innerHTML = 'Copied to clipboard';
                navigator.clipboard.writeText(hint);
                setTimeout(() => { el.innerHTML = hint }, 1000);
            }
        });
        this.root.querySelector('#hint-insert')?.addEventListener('pointerdown', (e) => {
            e.stopImmediatePropagation();
            const el = this.root.querySelector('#note-hint .code');
            if(el){
                const hint = el.innerHTML;
                this.emitActionEvent("hint-insert", hint);
            }
        });
        this.root.querySelector('#hint-history')?.addEventListener('pointerdown', (e) => {
            e.stopImmediatePropagation();
            const dialog = new NoteHistoryDialog(this.recorder);
            dialog.show();
        });
    }

    disconnectedCallback() { }

    attributeChangedCallback(name : string, oldValue : string, newValue : string) {
        if (newValue === oldValue) return;
        if (name === 'midi-in') {
            this.setMidiIn(newValue === 'true');
        }
        else if (name === 'midi-out') {
            this.setMidiOut(newValue === 'true');
        }
    }


    private setMidiLED(color : string) {
        const led = this.root.querySelector('#midi-indicator .led');
        led?.classList.remove('red', 'green', 'amber');
        if (color !== '') led?.classList.add(color);
    }

    private setMidiIn(on : boolean) {
        if (on === this.midiIn) return;
        this.setAttribute('midi-in', `${on}`);
        this.midiIn = on;
        if (on) {
            MIDIManager.init();
            MIDIManager.addListener(this);
            this.removeClass('#midi-indicator', 'hidden');
        } else {
            MIDIManager.removeListener(this);
            this.addClass('#midi-indicator', 'hidden');
        }
        this.setClass('#midi-indicator', 'hidden', !(this.midiIn || this.midiOut));
        this.setMidiLED((this.midiOut && this.midiIn) ? 'amber' : this.midiIn ? 'green' : this.midiOut ? 'red' : '');
    }

    private setMidiOut(on : boolean) {
        if (on === this.midiOut) return;
        this.setAttribute('midi-out', `${on}`);
        this.midiOut = on;
        this.setClass('#midi-indicator', 'hidden', !(this.midiIn || this.midiOut));
        this.setMidiLED((this.midiOut && this.midiIn) ? 'amber' : this.midiIn ? 'green' : this.midiOut ? 'red' : '');
        if (on) MIDIManager.init();
    }


    public setPatch(patch : SynthPatch, action : string) {

        this.setHTML('.patch-name', patch.name);
        this.root.querySelector(`context-menu-item[action="${action}"]`)?.setAttribute('checked', 'true');

        this.root.querySelector('#instrument')?.setAttribute('armed', 'false');

        // change instrument ui
        const instrument = ('instrument' in patch) ? patch.instrument : 'piano';

        const el = (instrument === 'drums') ? 
            document.createElement('drums-instrument') : 
            document.createElement('piano-instrument');
        el.id = 'instrument';


        const wrapper = this.root.querySelector('.instrument-wrapper') as HTMLDivElement;
        if (wrapper.hasChildNodes()) {
            wrapper.replaceChild(el, this.root.querySelector('#instrument')!);
        } else {
            wrapper.append(el);
        }

        el.addEventListener('note-on', e => {
            e.stopImmediatePropagation();
            this.relayEvent(e as CustomEvent);
            this.recorder.noteOn((e as CustomEvent).detail.note);
            this.setNoteHint(this.recorder.currentHint);
        });
        el.addEventListener('note-off', e => {
            e.stopImmediatePropagation();
            this.relayEvent(e as CustomEvent);
            this.recorder.noteOff((e as CustomEvent).detail.note);
        });

        ((el as unknown) as Instrument).setPatch(patch);
        if (instrument === 'piano') el.setAttribute('key-range', '17');
    }


    private relayEvent(e : CustomEvent) {
        this.dispatchEvent(new CustomEvent(e.type, { bubbles: false, composed: true, detail: e.detail }));
    }

    private emitNoteEvent(name : string, note : Note, source : string) {
        this.dispatchEvent(new CustomEvent(name, {
            bubbles: false,
            composed: true,
            detail: { note: note.note, velocity: note.velocity, source: source }
        }));
    }

    private emitCustomEvent(name : string, data : any = { }) {
        this.dispatchEvent(new CustomEvent(name, {
            bubbles: false,
            composed : true,
            detail: data
        }));
    }

    private emitUpdateEvent(property : string, value : any) {
        this.dispatchEvent(new CustomEvent("update", {
            bubbles: false,
            composed: true,
            detail: { property, value }
        }));
    }

    private emitActionEvent(property : string, value : any) {
        this.dispatchEvent(new CustomEvent("action", {
            bubbles: false,
            composed: true,
            detail: { property, value }
        }));
    }

    onMidiInput(event : MIDIEvent) {
        const note = new Note(event.note);
        note.velocity = event.velocity;
        if (event.message === 'note-on') {
            this.emitNoteEvent('note-on', note, 'midi');
            this.showNoteOn(note);
            this.recorder.noteOn(note);
            this.setNoteHint(this.recorder.currentHint);
        }
        else if (event.message === 'note-off') {
            this.emitNoteEvent('note-off', note, 'midi');
            this.showNoteOff(note);
            this.recorder.noteOff(note);
        }
    }

    private setNoteHint(hint : string) {
        const el = this.root.querySelector('#note-hint .code');
        if (el) el.innerHTML = hint;
        this.root.querySelector('#note-hint')?.classList.add('show');
    }


    public showNoteOn(note : Note) {
        const el = (this.root.querySelector('#instrument') as unknown);
        if (el) { (el as Instrument).noteOn(note.note, note.velocity); }
    }

    public showNoteOff(note : Note) {
        const el = (this.root.querySelector('#instrument') as unknown);
        if (el) { (el as Instrument).noteOff(note.note); }
    }

    public allNotesOff() {
        const el = (this.root.querySelector('#instrument') as unknown);
        if (el) { (el as Instrument).allNotesOff(); }
    }


    private async buildPatchMenu() {
        const menu = this.root.querySelector('#instrument-menu');
        if (menu) menu.innerHTML = patchMenu;
        menu?.addEventListener('context-menu-action', async (e) => {
            this.setHTML('.patch-name', 'Loading...');
            const value = (e as CustomEvent).detail.action;
            if (value) {
                this.emitUpdateEvent('patch', value);
            }
        });
    }

    private addClass(selector : string, className : string) {
        const e = this.root.querySelector(selector);
        e?.classList.add(className);
    }

    private removeClass(selector : string, className : string) {
        const e = this.root.querySelector(selector);
        e?.classList.remove(className);
    }

    private setClass(selector : string, className : string, set : boolean) {
        const e = this.root.querySelector(selector);
        e?.classList.toggle(className, set);
    }

    private setHTML(selector : string, value : string) {
        const e = this.root.querySelector(selector);
        if (e) e.innerHTML = value;
    }
}


interface Instrument {
    noteOn(note : number, velocity : number) : void;

    noteOff(note : number) : void;

    allNotesOff() : void;

    isNoteOn(note : number) : boolean;

    armKeyboard() : void;

    disarmKeyboard() : void;

    get isKeyboardArmed() : boolean;

    setPatch(patch : any) : void;
}
